import React, { useRef, useEffect, useState, useCallback } from 'react';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import '../layout/sass/_pickListSugestoes.scss';

import '../layout/sass/_responsiveTable.scss';
import { cpf as cpfValidator } from 'cpf-cnpj-validator';
import api from '../api/api';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import LogoAgricultura from '../assets/Logo_Agricultura.png';
import LogoIEF from '../assets/Logo_IEF.png';
import { Dialog } from 'primereact/dialog';
import { InputMask } from 'primereact/inputmask';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { classNames } from 'primereact/utils';
import { Message } from 'primereact/message';
import { ErrorValidationMessage } from './ErrorValidationMessage';

export const FormPomarDomestico = ({ setSolicitarMudas, setCpf, setSomenteComprovante }) => {
    const toast = useRef(null);
    const [mudas, setMudas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const confirmationToast = useRef(null);
    const [sortable, setSortable] = useState('ASC');
    const [first, setFirst] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortableCampo, setSortableCampo] = useState('nome');
    const [visible, setVisible] = useState(false);

    // Estados para o fluxo de validação de datas da Campanha
    const [statusCampanha, setStatusCampanha] = useState('CARREGANDO');
    // Opções: 'CARREGANDO' | 'SEM_CAMPANHA' | 'EM_ANDAMENTO' | 'PERIODO_REIMPRESSAO' | 'FINALIZADA'
    const [campanhaInfo, setCampanhaInfo] = useState(null);

    const history = useHistory();
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 20,
        sortField: null,
        sortOrder: null,
    });

    const onSolicitarClick = () => {
        setVisible(true);
    };

    const schema = Yup.object().shape({
        cpf: Yup
            .string()
            .nullable()
            .required("O CPF é obrigatório!")
            .test("test-cpf",
                "O CPF inserido é inválido",
                (value) => {
                    if (value === undefined || value === '' || value === null)
                        return true;
                    return cpfValidator.isValid(value);
                }
            ),
    });

    const formik = useFormik({
        initialValues: { cpf: "" },
        validationSchema: schema,
        enableReinitialize: true,
        onSubmit: async (data) => {
            try {
                const perfilExiste = await api.get(`pessoa/perfilExiste/${data.cpf}`);
                
                // Identifica se é um fluxo exclusivo de reimpressão
                const ehReimpressao = statusCampanha === 'PERIODO_REIMPRESSAO' || (statusCampanha === 'EM_ANDAMENTO' && mudas.length === 0);

                if (perfilExiste.data === true) {
                    toast.current.show({ life: 2000, severity: 'success', summary: 'Cadastro encontrado!', detail: `Redirecionando...` });
                    
                    setTimeout(() => {
                        setCpf(data.cpf);
                        if (typeof setSomenteComprovante === 'function') {
                            setSomenteComprovante(ehReimpressao);
                        }
                        setSolicitarMudas(true);
                    }, 1500);
                } else {
                    if (ehReimpressao) {
                        toast.current.show({ life: 4000, severity: 'warn', summary: 'Nenhum cadastro encontrado!', detail: `Não há solicitações registradas para o CPF informado.` });
                    } else {
                        setCpf(data.cpf);
                        if (typeof setSomenteComprovante === 'function') {
                            setSomenteComprovante(false);
                        }
                        setSolicitarMudas(true);
                    }
                }
            } catch (err) {
                toast.current.show({ severity: 'error', summary: 'Erro ao consultar CPF', detail: err.toString() });
            }
        }
    });

    // Função para verificar o status de datas e buscar mudas
    const verificarCampanhaEObterMudas = useCallback(async (event) => {
        setIsLoading(true);
        setMudas([]);

        try {
            const campanhaAtivaResponse = await api.get('campanha/ativoCampanha');
            const campanha = campanhaAtivaResponse.data;

            if (!campanha || !campanha.ativo) {
                setStatusCampanha('SEM_CAMPANHA');
                setIsLoading(false);
                return;
            }

            setCampanhaInfo(campanha);

            const agora = new Date().getTime();

            const dataInicioStr = campanha.dataInicio;
            const dataFimStr = campanha.dataFim || campanha.dataTermino;
            const dataEntregaStr = campanha.dataEntrega;

            const dataInicio = dataInicioStr ? new Date(dataInicioStr).getTime() : 0;
            const dataFim = dataFimStr ? new Date(dataFimStr).getTime() : 0;
            const dataEntrega = dataEntregaStr ? new Date(dataEntregaStr).getTime() : 0;

            // 1. Antes de iniciar a campanha
            if (agora < dataInicio) {
                setStatusCampanha('AGUARDANDO_INICIO');
                setMudas([]);
                setTotalRecords(0);

                // 2. Período Ativo de Solicitação (Entre dataInicio e dataFim)
            } else if (agora >= dataInicio && agora <= dataFim) {
                setStatusCampanha('EM_ANDAMENTO');

                if (event !== undefined) {
                    setTimeout(() => setFirst(event.first), 250);
                }

                const hasFilters = lazyParams.filters && lazyParams.filters['nome']?.value;
                const filterNome = hasFilters ? lazyParams.filters['nome'].value : '';
                const sortField = hasFilters ? 'nome' : sortableCampo;
                const sortOrder = sortable;

                const baseUrl = `campanhaMudas/saldo/${campanha.id}`;
                const filterPath = filterNome ? `/${filterNome}` : '';
                const sortParam = `?sort=${sortField},${sortOrder}`;
                const url = `${baseUrl}${filterPath}${sortParam}`;

                const response = await api.get(url);
                const mudasFiltradas = response.data
                    .filter(muda => muda.saldo > 0)
                    .map(muda => muda.campanhaMudas.muda);

                setMudas(mudasFiltradas);
                setTotalRecords(response.data.totalElements || mudasFiltradas.length);

                // 3. Período de Reimpressão (Entre dataFim e dataEntrega)
            } else if (agora > dataFim && agora <= dataEntrega) {
                setStatusCampanha('PERIODO_REIMPRESSAO');
                setMudas([]);
                setTotalRecords(0);

                // 4. Campanha Totalmente Encerrada (Após dataEntrega)
            } else {
                setStatusCampanha('FINALIZADA');
                setMudas([]);
                setTotalRecords(0);
            }

        } catch (error) {
            console.error('Erro ao verificar campanha ou carregar mudas:', error);
            setStatusCampanha('SEM_CAMPANHA');
        } finally {
            setIsLoading(false);
        }
    }, [lazyParams, sortableCampo, sortable]);

    useEffect(() => {
        verificarCampanhaEObterMudas();
    }, [verificarCampanhaEObterMudas]);

    const header = (
        <div className="p-grid p-col-12 p-m-0 p-p-0">
            <div>
                <h2 style={{ textAlign: "center" }}>Uma parceria entre a Prefeitura e IEF</h2>
            </div>
            {statusCampanha === 'EM_ANDAMENTO' && (
                <div className="p-my-0 p-grid p-dir-rev p-col">
                    <div className="p-m-1">
                        {mudas.length > 0 ? (
                            <Button
                                label="Solicitar Mudas"
                                className="p-mb-2 p-button-primary"
                                icon="pi pi-check"
                                iconPos="left"
                                type="button"
                                onClick={onSolicitarClick}
                            />
                        ) : (
                            <Button
                                label="Imprimir Comprovante"
                                className="p-mb-2 p-button-warning"
                                icon="pi pi-print"
                                iconPos="left"
                                type="button"
                                onClick={onSolicitarClick}
                            />
                        )}
                    </div>
                </div>
            )}
            {statusCampanha === 'PERIODO_REIMPRESSAO' && (
                <div className="p-my-0 p-grid p-dir-rev p-col">
                    <div className="p-m-1">
                        <Button
                            label="Imprimir Comprovante"
                            className="p-mb-2 p-button-warning"
                            icon="pi pi-print"
                            iconPos="left"
                            type="button"
                            onClick={onSolicitarClick}
                        />
                    </div>
                </div>
            )}
        </div>
    );

    const nomeBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <p style={{ textAlign: 'left' }}>
                    <span className="p-column-title">Nome</span>
                    <span className="table-data">{rowData.nome}</span>
                </p>
            </React.Fragment>
        );
    };

    const onSort = () => {
        if (sortable === 'ASC') {
            setSortable('DESC');
        } else if (sortable === 'DESC') {
            setSortable('ASC');
        }
    };

    const onFilter = (event) => {
        event['first'] = 0;
        setLazyParams(event);
    };

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    return (
        <>
            <div className="card p-grid p-col-12 p-mx-0 p-p-0 p-mt-0">
                <Toast ref={toast} position="bottom-right" />
                <Toast ref={confirmationToast} position="bottom-center" />

                {/* Modal de CPF */}
                <Dialog
                    className='p-scrollpanel-bar-y'
                    header={(statusCampanha === 'PERIODO_REIMPRESSAO' || (statusCampanha === 'EM_ANDAMENTO' && mudas.length === 0)) ? "Digite seu CPF para consultar e reimprimir" : "Digite o seu CPF"}
                    onHide={() => setVisible(false)}
                    visible={visible}
                    breakpoints={{ '960px': '75vw', '640px': '100vw' }}
                    style={{ width: '35vw' }}
                >
                    <div className="p-field p-col-12">
                        <label
                            htmlFor="cpf"
                            className={classNames({ 'p-error': isFormFieldValid('cpf') })}>
                            CPF
                        </label>
                        <InputMask
                            id="cpf"
                            name="cpf"
                            mask="999.999.999-99"
                            unmask={true}
                            value={formik.values.cpf}
                            className={classNames({ 'p-invalid': isFormFieldValid('cpf') })}
                            onChange={formik.handleChange}
                            style={{ color: '#000' }}
                        />
                        {formik.errors.cpf && formik.touched.cpf &&
                            <ErrorValidationMessage message={formik.errors.cpf} />
                        }
                    </div>
                    <div className="p-d-flex p-jc-end p-pt-2">
                        <Button
                            label={(statusCampanha === 'PERIODO_REIMPRESSAO' || (statusCampanha === 'EM_ANDAMENTO' && mudas.length === 0)) ? "Consultar / Reimprimir" : "Enviar"}
                            className="p-mb-2"
                            iconPos="right"
                            type="submit"
                            onClick={formik.handleSubmit}
                        />
                    </div>
                </Dialog>

                {/* Cabeçalho */}
                <div className="card p-col-12 p-md-12 p-lg-2"></div>

                <div className="card p-col-12 p-md-4 p-lg-2">
                    <img src={LogoAgricultura} style={{ margin: "20px 0 0 0" }} alt="Logo Agricultura" />
                </div>

                <div className="card p-col-12 p-md-4 p-lg-4">
                    <h2 style={{ textAlign: "center", width: "100%" }}>POMAR DOMÉSTICO - Relação de Mudas Disponíveis</h2>
                </div>

                <div className="card p-col-12 p-md-4 p-lg-2">
                    <img src={LogoIEF} style={{ margin: "0 0 0 0" }} alt="Logo IEF" />
                </div>

                <div className="card p-col-12 p-md-12 p-lg-2"></div>

                {/* Bloco de Mensagens por Estado de Data */}
                <div className="card p-col-12 p-md-12 p-lg-8" style={{ margin: '0 auto' }}>
                    {statusCampanha === 'SEM_CAMPANHA' && (
                        <Message
                            severity="warn"
                            text="Sem campanha ativa no momento."
                            className="p-p-3 p-col-12"
                            style={{ width: '100%' }}
                        />
                    )}

                    {statusCampanha === 'AGUARDANDO_INICIO' && (
                        <Message
                            severity="info"
                            text={`A campanha de solicitação de mudas será iniciada em ${new Date(campanhaInfo?.dataInicio).toLocaleString('pt-BR')}.`}
                            className="p-p-3 p-col-12"
                            style={{ width: '100%' }}
                        />
                    )}

                    {/* Alerta destacado quando não há mudas durante a campanha */}
                    {statusCampanha === 'EM_ANDAMENTO' && !isLoading && mudas.length === 0 && (
                        <div className="p-col-12 p-p-0 p-mb-3">
                            <Message
                                severity="warn"
                                text="Sem mudas para solicitar no momento. Você pode apenas consultar ou reimprimir seu comprovante."
                                className="p-p-3 p-col-12"
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}

                    {statusCampanha === 'PERIODO_REIMPRESSAO' && (
                        <div className="p-col-12 p-p-0 p-mb-3">
                            <Message
                                severity="info"
                                text={`O período de solicitação de mudas foi encerrado em ${new Date(campanhaInfo?.dataFim || campanhaInfo?.dataTermino).toLocaleString('pt-BR')}. Você pode apenas reimprimir o seu comprovante até ${new Date(campanhaInfo?.dataEntrega).toLocaleString('pt-BR')}.`}
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}

                    {statusCampanha === 'FINALIZADA' && (
                        <Message
                            severity="error"
                            text="A campanha de distribuição de mudas e o período de reimpressão foram encerrados."
                            className="p-p-3 p-col-12"
                            style={{ width: '100%' }}
                        />
                    )}
                </div>

                {/* Tabela disponível no período ativo */}
                {statusCampanha === 'EM_ANDAMENTO' && (
                    <div className="card p-col-12 p-md-12 p-lg-8 datatable-responsive-demo" style={{ margin: '0 auto' }}>
                        <div className="p-fluid">
                            <DataTable
                                totalRecords={totalRecords}
                                lazy="true"
                                first={first}
                                onPage={verificarCampanhaEObterMudas}
                                header={header}
                                value={mudas}
                                filterDisplay="row"
                                className="p-datatable-responsive-demo"
                                emptyMessage="Nenhuma muda encontrada com saldo disponível"
                                loading={isLoading}
                                dataKey="id"
                                responsiveLayout="scroll"
                                onSort={onSort}
                                onFilter={onFilter}
                                filters={lazyParams.filters}
                            >
                                <Column
                                    field="nome"
                                    header="Nome"
                                    sortable
                                    body={nomeBodyTemplate}
                                    lazyParams
                                    filter
                                    filterMatchMode="contains"
                                    filterPlaceholder="Pesquisar muda"
                                    style={{ padding: "2px" }}
                                />
                            </DataTable>
                        </div>
                    </div>
                )}

                {/* Botão simples de acionamento em período de reimpressão */}
                {statusCampanha === 'PERIODO_REIMPRESSAO' && (
                    <div className="card p-col-12 p-md-12 p-lg-8" style={{ margin: '0 auto', textAlign: 'center' }}>
                        <Button
                            label="Clique aqui para Reimprimir Comprovante"
                            className="p-button-lg p-button-warning"
                            icon="pi pi-print"
                            type="button"
                            onClick={onSolicitarClick}
                        />
                    </div>
                )}
            </div>
        </>
    );
};